import {
  SqliteClient,
  TypeId,
  currentTransferables,
  layer,
  layerConfig,
  layerMemory,
  layerMemoryConfig,
  make,
  makeMemory,
  withTransferables
} from "./chunk-C4KMPB4F.mjs";
import {
  DEFAULT_JOURNAL_MODE,
  DEFAULT_SYNCHRONOUS,
  applyOpfsPragmas,
  checkpointWal
} from "./chunk-6A53PZGM.mjs";
import {
  __export
} from "./chunk-IEP6GGEX.mjs";

// src/SqliteClient.ts
var SqliteClient_exports2 = {};
__export(SqliteClient_exports2, {
  SqliteClient: () => SqliteClient,
  TypeId: () => TypeId,
  currentTransferables: () => currentTransferables,
  layer: () => layer,
  layerConfig: () => layerConfig,
  layerMemory: () => layerMemory,
  layerMemoryConfig: () => layerMemoryConfig,
  layerOpfs: () => layerOpfs,
  make: () => make,
  makeMemory: () => makeMemory,
  makeOpfs: () => makeOpfs,
  withTransferables: () => withTransferables
});

// src/internal/opfs-client.ts
import * as Reactivity from "@effect/experimental/Reactivity";
import * as Client from "@effect/sql/SqlClient";
import * as SqlError from "@effect/sql/SqlError";
import * as Statement from "@effect/sql/Statement";
import * as WaSqlite from "@dxos/wa-sqlite";
import SQLiteESMFactory from "@dxos/wa-sqlite/dist/wa-sqlite.mjs";
import * as Chunk from "effect/Chunk";
import * as Context from "effect/Context";
import * as Effect from "effect/Effect";
import { identity } from "effect/Function";
import * as GlobalValue from "effect/GlobalValue";
import * as Layer from "effect/Layer";
import * as Scope from "effect/Scope";
import * as Stream from "effect/Stream";
import { log } from "@dxos/log";
import { AccessHandlePoolVFS } from "@dxos/wa-sqlite/src/examples/AccessHandlePoolVFS.js";
var __dxlog_file = "/__w/dxos/dxos/packages/common/sql-sqlite/src/internal/opfs-client.ts";
var ATTR_DB_SYSTEM_NAME = "db.system.name";
var DEFAULT_VFS_DIRECTORY = "opfs";
var initModule = Effect.runSync(Effect.cached(Effect.promise(() => SQLiteESMFactory())));
var initEffect = Effect.runSync(Effect.cached(initModule.pipe(Effect.map((module) => WaSqlite.Factory(module)))));
var registeredVfs = GlobalValue.globalValue("@dxos/sql-sqlite/opfs-vfs-registered", () => /* @__PURE__ */ new Set());
var vacuumDatabase = (sqlite3, db) => {
  for (const stmt of sqlite3.statements(db, "VACUUM")) {
    let result = sqlite3.step(stmt);
    while (result === WaSqlite.SQLITE_ROW) {
      result = sqlite3.step(stmt);
    }
    if (result !== WaSqlite.SQLITE_DONE) {
      throw new Error("VACUUM failed while persisting imported database");
    }
  }
};
var importDatabase = (sqlite3, db, data, pragmaOptions) => {
  sqlite3.deserialize(db, "main", data, data.length, data.length, 1 | 2);
  vacuumDatabase(sqlite3, db);
  applyOpfsPragmas(sqlite3, db, {
    journalMode: pragmaOptions.journalMode ?? DEFAULT_JOURNAL_MODE,
    synchronous: pragmaOptions.synchronous ?? DEFAULT_SYNCHRONOUS
  });
};
var recordSqliteQueryMetrics = (sql, params, resultCount, begin) => {
  const end = performance.now();
  log("sqlite query", {
    sql,
    params,
    results: resultCount,
    time: end - begin
  }, { "~LogMeta": "~LogMeta", F: __dxlog_file, L: 51, S: void 0 });
  performance.measure(sql.slice(0, 128), {
    start: begin,
    end,
    detail: {
      devtools: {
        dataType: "track-entry",
        track: "Query",
        trackGroup: "SQlite",
        color: "tertiary-dark",
        properties: [
          [
            "sql",
            sql
          ],
          [
            "params",
            params
          ],
          [
            "resultCount",
            resultCount
          ]
        ]
      }
    }
  });
};
var makeOpfs = (options) => Effect.gen(function* () {
  const reactivity = yield* Reactivity.Reactivity;
  const compiler = Statement.makeCompilerSqlite(options.transformQueryNames);
  const transformRows = options.transformResultNames ? Statement.defaultTransforms(options.transformResultNames).array : void 0;
  const vfsDirectory = options.vfsDirectory ?? DEFAULT_VFS_DIRECTORY;
  const journalMode = options.journalMode ?? DEFAULT_JOURNAL_MODE;
  const synchronous = options.synchronous ?? DEFAULT_SYNCHRONOUS;
  const makeConnection = Effect.gen(function* () {
    const sqlite3 = yield* initEffect;
    if (!registeredVfs.has(vfsDirectory)) {
      registeredVfs.add(vfsDirectory);
      const factory = yield* initModule;
      const vfs = yield* Effect.promise(() => AccessHandlePoolVFS.create(vfsDirectory, factory));
      sqlite3.vfs_register(vfs, false);
    }
    const db = yield* Effect.acquireRelease(Effect.try({
      try: () => sqlite3.open_v2(options.dbName, void 0, vfsDirectory),
      catch: (cause) => new SqlError.SqlError({
        cause,
        message: "Failed to open database"
      })
    }), (handle) => Effect.sync(() => sqlite3.close(handle)));
    yield* Effect.try({
      try: () => applyOpfsPragmas(sqlite3, db, {
        journalMode,
        synchronous
      }),
      catch: (cause) => new SqlError.SqlError({
        cause,
        message: "Failed to configure database PRAGMAs"
      })
    });
    if (options.installReactivityHooks) {
      sqlite3.update_hook(db, (_op, _db, table, rowid) => {
        if (!table) {
          return;
        }
        reactivity.unsafeInvalidate({
          [table]: [
            String(Number(rowid))
          ]
        });
      });
    }
    const run = (sql, params = [], rowMode = "object") => Effect.try({
      try: () => {
        const results = [];
        const begin = performance.now();
        for (const stmt of sqlite3.statements(db, sql)) {
          let columns;
          sqlite3.bind_collection(stmt, params);
          while (sqlite3.step(stmt) === WaSqlite.SQLITE_ROW) {
            columns = columns ?? sqlite3.column_names(stmt);
            const row = sqlite3.row(stmt);
            if (rowMode === "object") {
              const obj = {};
              for (let index = 0; index < columns.length; index++) {
                obj[columns[index]] = row[index];
              }
              results.push(obj);
            } else {
              results.push(row);
            }
          }
        }
        recordSqliteQueryMetrics(sql, params, results.length, begin);
        return results;
      },
      catch: (cause) => {
        log("sqlite error", {
          error: cause,
          sql,
          params
        }, { "~LogMeta": "~LogMeta", F: __dxlog_file, L: 155, S: this });
        return new SqlError.SqlError({
          cause,
          message: "Failed to execute statement"
        });
      }
    });
    return identity({
      execute: (sql, params, rowTransform) => rowTransform ? Effect.map(run(sql, params), rowTransform) : run(sql, params),
      executeRaw: (sql, params) => run(sql, params),
      executeValues: (sql, params) => run(sql, params, "array"),
      executeUnprepared(sql, params, rowTransform) {
        return this.execute(sql, params, rowTransform);
      },
      executeStream: (sql, params, rowTransform) => {
        const stream = function* () {
          const begin = performance.now();
          let resultCount = 0;
          for (const stmt of sqlite3.statements(db, sql)) {
            let columns;
            sqlite3.bind_collection(stmt, params);
            while (sqlite3.step(stmt) === WaSqlite.SQLITE_ROW) {
              columns = columns ?? sqlite3.column_names(stmt);
              const row = sqlite3.row(stmt);
              const obj = {};
              for (let index = 0; index < columns.length; index++) {
                obj[columns[index]] = row[index];
              }
              resultCount++;
              yield obj;
            }
          }
          recordSqliteQueryMetrics(sql, params, resultCount, begin);
        };
        return Stream.suspend(() => Stream.fromIteratorSucceed(stream()[Symbol.iterator]())).pipe(rowTransform ? Stream.mapChunks((chunk) => Chunk.unsafeFromArray(rowTransform(Chunk.toReadonlyArray(chunk)))) : identity, Stream.mapError((cause) => {
          log("sqlite error", {
            error: cause,
            sql,
            params
          }, { "~LogMeta": "~LogMeta", F: __dxlog_file, L: 194, S: this });
          return new SqlError.SqlError({
            cause,
            message: "Failed to execute statement"
          });
        }));
      },
      export: Effect.try({
        // Checkpoint so the serialized snapshot (and the on-disk main file) reflects all
        // committed WAL frames; leaves the WAL empty so a later raw pool read stays correct.
        try: () => {
          checkpointWal(sqlite3, db);
          return sqlite3.serialize(db, "main");
        },
        catch: (cause) => new SqlError.SqlError({
          cause,
          message: "Failed to export database"
        })
      }),
      import: (data) => Effect.try({
        try: () => {
          log("opfs import", {
            bytes: data.byteLength
          }, { "~LogMeta": "~LogMeta", F: __dxlog_file, L: 219, S: this });
          importDatabase(sqlite3, db, data, options);
        },
        catch: (cause) => new SqlError.SqlError({
          cause,
          message: "Failed to import database"
        })
      })
    });
  });
  const semaphore = yield* Effect.makeSemaphore(1);
  const connection = yield* makeConnection;
  const acquirer = semaphore.withPermits(1)(Effect.succeed(connection));
  const transactionAcquirer = Effect.uninterruptibleMask((restore) => Effect.as(Effect.zipRight(restore(semaphore.take(1)), Effect.tap(Effect.scope, (scope2) => Scope.addFinalizer(scope2, semaphore.release(1)))), connection));
  return Object.assign(yield* Client.make({
    acquirer,
    compiler,
    transactionAcquirer,
    spanAttributes: [
      ...options.spanAttributes ? Object.entries(options.spanAttributes) : [],
      [
        ATTR_DB_SYSTEM_NAME,
        "sqlite"
      ]
    ],
    transformRows
  }), {
    // Object.assign widens unique symbol TypeId; same pattern as @effect/sql-sqlite-wasm makeMemory.
    [TypeId]: TypeId,
    config: options,
    export: semaphore.withPermits(1)(connection.export),
    import: (data) => semaphore.withPermits(1)(connection.import(data))
  });
});
var layerOpfs = (config) => Layer.scopedContext(Effect.map(makeOpfs(config), (client) => Context.make(SqliteClient, client).pipe(Context.add(Client.SqlClient, client)))).pipe(Layer.provide(Reactivity.layer));

export {
  makeOpfs,
  layerOpfs,
  SqliteClient_exports2 as SqliteClient_exports
};
//# sourceMappingURL=chunk-Z2YZ6DWX.mjs.map
